import React, {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  createContext,
} from 'react';
import { Tabs as TabsPrimitive } from '@base-ui/react/tabs';
import { useGlide } from './useGlide.js';

// Segmented tabs, the same pill as the toggle group. One tab is selected.
// The pill slides to it; a fainter pill previews the tab under the pointer.
// Arrows move focus; Enter or Space selects.

const TabsContext = createContext({
  size: 'default',
  value: undefined,
  disabled: false,
  hoveredIndex: null,
  setHoveredIndex: () => {},
});

export function Tabs({
  size = 'default',
  value: valueProp,
  defaultValue,
  onValueChange,
  disabled = false,
  className,
  children,
  ...props
}) {
  const [uncontrolled, setUncontrolled] = useState(defaultValue);
  // Panels fade in once the reader has switched tabs, not on first paint.
  const [switched, setSwitched] = useState(false);
  const value = valueProp !== undefined ? valueProp : uncontrolled;
  return (
    <TabsPrimitive.Root
      {...props}
      className={['edl-tabs', className].filter(Boolean).join(' ')}
      data-switched={switched ? '' : undefined}
      value={value}
      onValueChange={(next, details) => {
        if (valueProp === undefined) setUncontrolled(next);
        setSwitched(true);
        onValueChange?.(next, details);
      }}
    >
      <TabsContext.Provider value={{ size, value, disabled }}>
        {children}
      </TabsContext.Provider>
    </TabsPrimitive.Root>
  );
}

export function TabsList({ className, children, ...props }) {
  const { size, value, disabled } = useContext(TabsContext);
  const listRef = useRef(null);
  const mouseInside = useRef(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(null);
  const [rects, setRects] = useState([]);

  const entries = Children.toArray(children).filter(
    (child) => isValidElement(child) && typeof child.type !== 'string',
  );
  const selectedIndex = entries.findIndex((child) => child.props.value === value);
  const geometryKey = `${size}:${entries.map(childKey).join('|')}`;

  const measure = useCallback(() => {
    const root = listRef.current;
    if (!root) return;
    const next = readRects(root);
    setRects((prev) => (sameRects(prev, next) ? prev : next));
  }, []);

  useLayoutEffect(() => {
    const root = listRef.current;
    if (!root) return undefined;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    root.querySelectorAll('[data-tab-index]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [measure, geometryKey]);

  const selectedRect = selectedIndex >= 0 ? rects[selectedIndex] : null;
  const hoverRect = hoveredIndex != null ? rects[hoveredIndex] : null;
  const focusRect = focusedIndex != null ? rects[focusedIndex] : null;
  const hoveringOther = Boolean(selectedRect && hoverRect && hoveredIndex !== selectedIndex);
  const selectionMoves = useGlide(boxOf(selectedRect) ? selectedIndex : null);
  const focusMoves = useGlide(boxOf(focusRect) ? focusedIndex : null);

  const indexed = Children.map(children, (child, index) => (
    isValidElement(child) && typeof child.type !== 'string'
      ? cloneElement(child, { index })
      : child
  ));

  return (
    <TabsPrimitive.List
      {...props}
      className={['edl-tabs__list', className].filter(Boolean).join(' ')}
      data-size={size}
      data-disabled={disabled ? '' : undefined}
      ref={listRef}
      onMouseEnter={() => { mouseInside.current = true; }}
      // React drops mouseenter on a disabled button, so the hover from the tab
      // before it would stay. Native mouseover still bubbles here.
      onMouseOver={(event) => {
        if (event.target.closest?.('[data-tab-index][data-disabled]')) setHoveredIndex(null);
      }}
      onMouseLeave={() => {
        mouseInside.current = false;
        setHoveredIndex(null);
      }}
      onFocus={(event) => {
        const item = event.target.closest?.('[data-tab-index]');
        if (!item || !listRef.current?.contains(item)) return;
        const index = Number(item.dataset.tabIndex);
        setHoveredIndex(index);
        setFocusedIndex(item.matches(':focus-visible') ? index : null);
      }}
      onBlur={(event) => {
        if (listRef.current?.contains(event.relatedTarget)) return;
        setFocusedIndex(null);
        if (mouseInside.current) return;
        setHoveredIndex(null);
      }}
    >
      <TabsContext.Provider value={{ size, value, disabled, hoveredIndex, setHoveredIndex }}>
        {boxOf(selectedRect) && (
          <div
            className={['edl-tabs__selected', selectionMoves ? 'is-moving' : ''].filter(Boolean).join(' ')}
            style={place(selectedRect, hoveringOther ? 0.8 : 1)}
            aria-hidden="true"
          />
        )}
        <HoverPill
          from={selectedRect}
          to={hoverRect}
          active={hoveringOther && !disabled}
          mouseInside={mouseInside}
        />
        {boxOf(focusRect) && (
          <div
            className={['edl-tabs__ring', focusMoves ? 'is-moving' : ''].filter(Boolean).join(' ')}
            style={place(focusRect, undefined, 2)}
            aria-hidden="true"
          />
        )}
        {indexed}
      </TabsContext.Provider>
    </TabsPrimitive.List>
  );
}

export function TabsTab({ icon: Icon, className, children, index = 0, value, disabled: disabledProp, ...props }) {
  const { size, value: selected, disabled, hoveredIndex, setHoveredIndex } = useContext(TabsContext);
  const iconOnly = Boolean(Icon) && !hasLabel(children);
  const isDisabled = Boolean(disabled || disabledProp);
  // A disabled tab never previews or reads as hovered; the selected one still does.
  const active = (hoveredIndex === index && !isDisabled) || selected === value;
  return (
    <TabsPrimitive.Tab
      {...props}
      value={value}
      disabled={isDisabled}
      className={['edl-tabs__tab', className].filter(Boolean).join(' ')}
      data-tab-index={index}
      data-icon-only={iconOnly ? '' : undefined}
      data-active={active ? '' : undefined}
      onMouseEnter={() => setHoveredIndex(isDisabled ? null : index)}
    >
      {Icon ? <Icon size={size === 'compact' ? 14 : 16} strokeWidth={1.5} aria-hidden="true" /> : null}
      {hasLabel(children) ? children : null}
    </TabsPrimitive.Tab>
  );
}

export function TabsPanel({ className, ...props }) {
  return <TabsPrimitive.Panel {...props} className={['edl-tabs__panel', className].filter(Boolean).join(' ')} />;
}

function HoverPill({ from, to, active, mouseInside }) {
  const [phase, setPhase] = useState('off');
  const [at, setAt] = useState(null);
  const [opacity, setOpacity] = useState(0);
  const alive = useRef(false);
  const phaseRef = useRef('off');
  const fromKey = rectKey(from);
  const toKey = rectKey(to);
  const go = (next) => {
    phaseRef.current = next;
    setPhase(next);
  };

  useLayoutEffect(() => {
    if (active && from && to) {
      if (!alive.current) {
        alive.current = true;
        go('enter');
        setAt(from);
        setOpacity(0);
        const id = requestAnimationFrame(() => {
          go('on');
          setAt(to);
          setOpacity(0.4);
        });
        return () => cancelAnimationFrame(id);
      }
      go('on');
      setAt(to);
      setOpacity(0.4);
      return undefined;
    }
    if (alive.current) {
      alive.current = false;
      if (!mouseInside.current && from) {
        go('back');
        setAt(from);
        setOpacity(0);
      } else {
        go('fade');
        setOpacity(0);
      }
    }
    if (phaseRef.current === 'off') return undefined;
    const id = window.setTimeout(() => go('off'), 180);
    return () => window.clearTimeout(id);
  }, [active, from, to, fromKey, toKey, mouseInside]);

  if (phase === 'off' || !at) return null;
  return (
    <div
      className="edl-tabs__hover"
      data-phase={phase}
      style={place(at, opacity)}
      aria-hidden="true"
    />
  );
}

function boxOf(rect) {
  return rect && rect.width > 0 && rect.height > 0 ? rect : null;
}

function place(rect, opacity, outset = 0) {
  return {
    transform: `translate(${rect.left - outset}px, ${rect.top - outset}px)`,
    width: rect.width + outset * 2,
    height: rect.height + outset * 2,
    opacity,
  };
}

function readRects(root) {
  return [...root.querySelectorAll('[data-tab-index]')]
    .sort((a, b) => Number(a.dataset.tabIndex) - Number(b.dataset.tabIndex))
    .map((el) => ({
      left: el.offsetLeft,
      top: el.offsetTop,
      width: el.offsetWidth,
      height: el.offsetHeight,
    }));
}

function sameRects(a, b) {
  return a.length === b.length && a.every((rect, i) => rectKey(rect) === rectKey(b[i]));
}

function rectKey(rect) {
  return rect ? `${rect.left},${rect.top},${rect.width},${rect.height}` : '';
}

function childKey(child) {
  const icon = child.props.icon;
  const name = icon?.displayName || icon?.name || '';
  return `${child.props.value}:${name}:${labelText(child.props.children)}`;
}

function hasLabel(children) {
  return labelText(children) !== '';
}

function labelText(children) {
  return Children.toArray(children)
    .filter((child) => child != null && child !== false)
    .join('');
}
